import { useMemo } from 'react';
import { pickBy, isArray, size, some, isString, isNumber } from 'lodash';
import { useTranslation } from 'react-i18next';
import { filtersTypesToRenderWithTypeName, FilterTypes } from '../constants/initialFilters';
import nonAudiBrandsFullList from '../constants/listOfNonAudiBrands.json';
import { NonAudiModelFullList } from './useFilterOptions';

type StringFilterTagsTypes = {
  filterType: string;
  label: string;
  preLabel: string | null;
  showType: boolean;
  value: string;
  isRemovable?: boolean;
};

const useFilterTagProcessor = (
  confirmedFilters: FilterTypes,
  listOfAllFilters: any,
  isTier3page: boolean,
) => {
  const { t } = useTranslation();

  // Grab all typeof String filters and filter only with values
  const availableStringFilters = useMemo(() => {
    return pickBy(
      confirmedFilters,
      (value) => isArray(value) && size(value) > 0 && some(value, isString),
    );
  }, [confirmedFilters]);

  // Grab all typeof Number filters and filter only with values
  const availableNumberFilters = useMemo(() => {
    return pickBy(
      confirmedFilters,
      (value) => isArray(value) && size(value) > 0 && some(value, isNumber),
    );
  }, [confirmedFilters]);

  const shouldFilterNonAudi = useMemo(() => {
    if (confirmedFilters.vehicleType === 'new') return false;
    if (confirmedFilters.nonAudi === 'foreign-brand.yes') return true;
    if (confirmedFilters.usedInventoryType === 'has-warranties.combined') return true;
    return false;
  }, [confirmedFilters.nonAudi, confirmedFilters.usedInventoryType, confirmedFilters.vehicleType]);

  // Transform all string filters to onjects with data
  const renderStringFilters: StringFilterTagsTypes[] = useMemo(() => {
    const listOfFilters = Object.keys(availableStringFilters)?.filter((filterName) => {
      if (filterName === 'partnerDealers') return false;
      if (!shouldFilterNonAudi && (filterName === 'otherMakes' || filterName === 'nonAudiModels'))
        return false;
      return true;
    });

    const tagsToRender = listOfFilters.map((filterType) => {
      const selectedFilters = availableStringFilters[filterType] as string[];

      const filterTypeOptions = (listOfAllFilters as any)[filterType];

      const showPreLabel = !!filtersTypesToRenderWithTypeName.includes(filterType);

      const transformedFilters = selectedFilters.map((filterValue: string) => {
        // Create same object structure for partner dealer to use label & value
        // For nonAudi Model we are adding make label at the front
        if (filterType === 'otherMakes') {
          const makeCode = filterValue as keyof typeof nonAudiBrandsFullList;
          return {
            value: filterValue,
            label: nonAudiBrandsFullList[makeCode]?.text,
            preLabel: showPreLabel ? t(`vlp.filters.filterOptions.${filterType}.tagLabel`) : null,
            showType: showPreLabel,
            filterType,
            isRemovable: true,
          };
        }
        if (filterType === 'nonAudiModels') {
          const makeCode = filterValue?.substring(0, 2) as keyof typeof nonAudiBrandsFullList;
          const modelCodeItems = nonAudiBrandsFullList[makeCode]?.members as NonAudiModelFullList;
          const modelCode = filterValue as keyof typeof modelCodeItems;
          return {
            value: filterValue,
            label: modelCodeItems[modelCode]?.text ?? 'Other',
            preLabel: showPreLabel ? t(`vlp.filters.filterOptions.${filterType}.tagLabel`) : null,
            showType: showPreLabel,
            filterType,
            isRemovable: true,
          };
        }
        const filterLabel =
          filterType === 'nonAudiModels'
            ? `${filterTypeOptions[filterValue]?.makeLabel} ${filterTypeOptions[filterValue]?.label}`
            : filterTypeOptions[filterValue]?.label;
        return {
          value: filterTypeOptions[filterValue]?.value,
          label: filterLabel,
          preLabel: showPreLabel ? t(`vlp.filters.filterOptions.${filterType}.tagLabel`) : null,
          showType: showPreLabel,
          filterType,
          isRemovable: !filterTypeOptions[filterValue]?.isFilterTagNotRemovable,
        };
      });
      return transformedFilters;
    });
    return ([] as StringFilterTagsTypes[]).concat(...tagsToRender);
  }, [availableStringFilters, listOfAllFilters, shouldFilterNonAudi, t]);

  // Transform all number filters to objects with data
  const renderNumberRangeFilters = useMemo(() => {
    const listOfFilters = Object.keys(availableNumberFilters);
    const tagsToRender = listOfFilters.map((filterType) => {
      const selectedFilters = availableNumberFilters[filterType] as number[];
      const showPreLabel = !!filtersTypesToRenderWithTypeName.includes(filterType);
      const filterTypeOptions = (listOfAllFilters as any)[filterType];

      const min = filterTypeOptions.min[selectedFilters[0]];
      const max = filterTypeOptions.max[selectedFilters[1]];

      const isRemovable = !min?.isFilterTagNotRemovable && !max?.isFilterTagNotRemovable;
      return {
        value: selectedFilters,
        label: `${t(`vlp.filters.filterOptions.${filterType}.${selectedFilters[0]}`)} - ${t(
          `vlp.filters.filterOptions.${filterType}.${selectedFilters[1]}`,
        )}`,
        preLabel: showPreLabel ? t(`vlp.filters.filterOptions.${filterType}.tagLabel`) : null,
        showType: showPreLabel,
        filterType,
        isRemovable,
      };
    });
    return tagsToRender;
  }, [availableNumberFilters, listOfAllFilters, t]);

  const filterCount = useMemo(() => {
    if (isTier3page) {
      return (
        renderStringFilters.filter((singleFilter) => singleFilter.isRemovable).length +
        renderNumberRangeFilters.filter((singleFilter) => singleFilter.isRemovable).length
      );
    }
    return renderStringFilters.length + renderNumberRangeFilters.length;
  }, [isTier3page, renderNumberRangeFilters, renderStringFilters]);

  return { renderStringFilters, renderNumberRangeFilters, filterCount };
};

export default useFilterTagProcessor;
